import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Eye, Search, X, Loader2, CirclePlus } from 'lucide-react';
import { fetchStockMetadata } from '../../utils/stockMetadataBlob';
import { formatCompanyName } from '../../utils/formatters';

const GreenPupilEye = ({ className, isActive = true }: { className?: string, isActive?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle 
      cx="12" cy="12" r="3" 
      fill={isActive ? "#22c55e" : "none"} 
      stroke={isActive ? "#22c55e" : "currentColor"} 
    />
  </svg>
);

interface StockData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  lastUpdate?: string;
}

interface WatchlistProps {
  selectedStock: string;
  onStockSelect: (symbol: string) => void;
  showFavoritesOnly?: boolean;
}

const MAX_FAVORITES = 20;

export function Watchlist({ selectedStock, onStockSelect }: WatchlistProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<string[]>(() => {
    const saved = localStorage.getItem('watchlist-favorites');
    return saved ? JSON.parse(saved) : ['BBRI', 'BBCA', 'BMRI'];
  });
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [stocksData, setStocksData] = useState<StockData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; symbol: string } | null>(null);

  useEffect(() => {
    localStorage.setItem('watchlist-favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    let isMounted = true;
    const loadSnapshot = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const rows = await fetchStockMetadata();
        const stocks: StockData[] = rows.map((stock) => {
          const item: StockData = {
            symbol: stock.symbol,
            name: stock.name || stock.symbol,
            price: stock.price,
            change: stock.change,
            changePercent: stock.changePercent,
          };
          if (stock.lastUpdate !== undefined) item.lastUpdate = stock.lastUpdate;
          return item;
        });
        if (isMounted) setStocksData(stocks);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load watchlist data');
          setStocksData([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadSnapshot();
    return () => { isMounted = false; };
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const toggleFavorite = (symbol: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFavorites(prev => {
      if (prev.includes(symbol)) return prev.filter(f => f !== symbol);
      if (prev.length >= MAX_FAVORITES) return prev; // max 20
      return [...prev, symbol];
    });
  };

  const removeFavorite = (symbol: string) => {
    setFavorites(prev => prev.filter(f => f !== symbol));
    setContextMenu(null);
  };

  const handleContextMenu = (e: React.MouseEvent, symbol: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, symbol });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchDropdown(value.length > 0);
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSearchDropdown(false), 200);
  };

  const handleStockSelectFromSearch = (symbol: string) => {
    onStockSelect(symbol);
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!normalizedSearchQuery) return [];
    
    const filtered = stocksData.filter(stock => 
      stock.symbol.toLowerCase().includes(normalizedSearchQuery) || 
      stock.name.toLowerCase().includes(normalizedSearchQuery)
    );

    return filtered.sort((a, b) => {
      const aSymbol = a.symbol.toLowerCase();
      const bSymbol = b.symbol.toLowerCase();
      
      const aExact = aSymbol === normalizedSearchQuery;
      const bExact = bSymbol === normalizedSearchQuery;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aSymbol.startsWith(normalizedSearchQuery);
      const bStarts = bSymbol.startsWith(normalizedSearchQuery);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      const aIncludes = aSymbol.includes(normalizedSearchQuery);
      const bIncludes = bSymbol.includes(normalizedSearchQuery);
      if (aIncludes && !bIncludes) return -1;
      if (!aIncludes && bIncludes) return 1;

      return aSymbol.localeCompare(bSymbol);
    }).slice(0, 30);
  }, [normalizedSearchQuery, stocksData]);

  // Main list: only favorites, max 20, ordered by favorites array
  const favoriteStocks = useMemo(() => {
    return favorites
      .slice(0, MAX_FAVORITES)
      .map(sym => stocksData.find(s => s.symbol === sym))
      .filter(Boolean) as StockData[];
  }, [favorites, stocksData]);

  const selectedStockData = useMemo(() => {
    return stocksData.find(s => s.symbol === selectedStock);
  }, [stocksData, selectedStock]);

  const selectedStockName = selectedStockData ? formatCompanyName(selectedStockData.name) : formatCompanyName(selectedStock);

  return (
    <Card className="h-full flex flex-col gap-0 overflow-hidden relative rounded border border-border">
      <CardHeader className="p-2 pb-1.5 px-[5%] z-10 bg-background">
        <CardTitle className="mb-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1.5 overflow-hidden">
              <span className="text-sm font-bold truncate">{selectedStock}</span>
              {selectedStockData && (
                <>
                  <span className="text-sm font-bold font-mono ml-1">{selectedStockData.price.toLocaleString()}</span>
                  <span className={`text-[10px] font-semibold px-1 rounded ${
                    selectedStockData.change >= 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  }`}>
                    {selectedStockData.change >= 0 ? '+' : ''}{selectedStockData.change} ({selectedStockData.changePercent.toFixed(2)}%)
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 pl-1">
              <GreenPupilEye className="w-3.5 h-3.5 text-muted-foreground" />
              {favorites.length}/{MAX_FAVORITES}
            </div>
          </div>
        </CardTitle>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            type="text"
            placeholder="Search & add stocks..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery.length > 0 && setShowSearchDropdown(true)}
            onBlur={handleSearchBlur}
            className="w-full pl-10 pr-8 py-2 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setShowSearchDropdown(false); }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {showSearchDropdown && searchResults.length > 0 && (
            <div 
              className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto"
              onMouseDown={(e) => e.preventDefault()}
            >
              {searchResults.map(stock => {
                const isFav = favorites.includes(stock.symbol);
                const changeClass = stock.change === 0 ? 'text-muted-foreground' : stock.change > 0 ? 'text-green-600' : 'text-red-600';
                const badgeVariant = stock.changePercent === 0 ? 'secondary' : stock.changePercent > 0 ? 'default' : 'destructive';
                return (
                  <div
                    key={stock.symbol}
                    className="flex items-center justify-between p-2.5 hover:bg-muted/50 cursor-pointer border-b border-border/50 last:border-b-0"
                    onClick={() => handleStockSelectFromSearch(stock.symbol)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-[13px] text-card-foreground">{stock.symbol}</span>
                        <Badge variant={badgeVariant} className="text-[9px] px-1 py-0 h-3.5 flex items-center gap-0.5">
                          {stock.changePercent > 0 ? <TrendingUp className="w-2 h-2" /> : stock.changePercent < 0 ? <TrendingDown className="w-2 h-2" /> : null}
                          {stock.changePercent === 0 ? '--' : `${Math.abs(stock.changePercent).toFixed(2)}%`}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{formatCompanyName(stock.name)}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <div className="text-right">
                        <p className="font-medium text-[13px] text-card-foreground">{stock.price ? stock.price.toLocaleString() : '--'}</p>
                        <p className={`text-[11px] ${changeClass}`}>{stock.change === 0 ? '--' : `${stock.change > 0 ? '+' : ''}${stock.change.toFixed(0)}`}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(stock.symbol); }}
                        className="hover:scale-110 transition-transform p-1 flex-shrink-0"
                        title={isFav ? 'Remove from watchlist' : favorites.length >= MAX_FAVORITES ? 'Watchlist full (max 20)' : 'Add to watchlist'}
                      >
                        {isFav ? (
                          <GreenPupilEye className="w-4 h-4 text-muted-foreground" isActive={isFav} />
                        ) : (
                          <CirclePlus className={`w-4 h-4 ${favorites.length >= MAX_FAVORITES ? 'text-muted-foreground/40' : 'text-muted-foreground hover:text-foreground'}`} />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-1.5 pt-0.5 px-[5%]">
        <div className="space-y-0.5">
          {isLoading ? (
            <div className="space-y-1.5 pt-1">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded border border-border animate-pulse">
                  <div className="flex-1 min-w-0 space-y-2 py-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-700/20 dark:bg-slate-700/50"></div>
                      <div className="h-3 w-12 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                      <div className="h-3 w-8 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                    </div>
                    <div className="h-2 w-24 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                  </div>
                  <div className="text-right space-y-2">
                    <div className="h-3 w-10 bg-slate-700/20 dark:bg-slate-700/50 rounded ml-auto"></div>
                    <div className="h-2 w-6 bg-slate-700/20 dark:bg-slate-700/50 rounded ml-auto"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-red-600 mb-2">{error}</p>
            </div>
          ) : favoriteStocks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <GreenPupilEye className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" isActive={false} />
              <p className="text-sm font-medium">No watchlist yet</p>
              <p className="text-xs mt-1">Search stocks above and click the + icon to add</p>
            </div>
          ) : (
            favoriteStocks.map(stock => {
              const changeClass = stock.change === 0 ? 'text-muted-foreground' : stock.change > 0 ? 'text-green-600' : 'text-red-600';
              const badgeVariant = stock.changePercent === 0 ? 'secondary' : stock.changePercent > 0 ? 'default' : 'destructive';
              return (
                <div
                  key={stock.symbol}
                  className={`flex items-center justify-between py-1.5 px-2 rounded border cursor-pointer transition-all duration-200 hover:shadow-sm
                    ${selectedStock === stock.symbol ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/50'}`}
                  onClick={() => onStockSelect(stock.symbol)}
                  onContextMenu={(e) => handleContextMenu(e, stock.symbol)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <button
                        onClick={(e) => toggleFavorite(stock.symbol, e)}
                        className="hover:scale-110 transition-transform flex-shrink-0"
                        title="Remove from watchlist"
                      >
                        <GreenPupilEye className="w-3 h-3 text-muted-foreground" />
                      </button>
                      <span className={`font-semibold text-[13px] ${selectedStock === stock.symbol ? 'text-primary' : 'text-card-foreground'}`}>
                        {stock.symbol}
                      </span>
                      <Badge variant={badgeVariant} className="text-[9px] px-1 py-0 h-3.5 flex items-center gap-0.5 ml-0.5">
                        {stock.changePercent > 0 ? <TrendingUp className="w-2 h-2" /> : stock.changePercent < 0 ? <TrendingDown className="w-2 h-2" /> : null}
                        {Number.isFinite(stock.changePercent) ? `${Math.abs(stock.changePercent).toFixed(2)}%` : '--'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate pr-2">{formatCompanyName(stock.name)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-semibold text-[13px] ${selectedStock === stock.symbol ? 'text-primary' : 'text-card-foreground'}`}>
                      {Number.isFinite(stock.price) ? stock.price.toLocaleString() : '--'}
                    </p>
                    <p className={`text-[11px] ${changeClass}`}>
                      {Number.isFinite(stock.change) ? `${stock.change > 0 ? '+' : ''}${stock.change.toFixed(0)}` : '--'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
      {contextMenu && (
        <div
          className="fixed z-[100] bg-popover text-popover-foreground rounded-md shadow-md border border-border py-1 text-sm min-w-[150px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground text-destructive flex items-center gap-2"
            onClick={() => {
              removeFavorite(contextMenu.symbol);
            }}
          >
            <GreenPupilEye className="w-4 h-4" isActive={false} />
            Remove from Watchlist
          </button>
        </div>
      )}
    </Card>
  );
}
